import { Cart, Cart_Item, GetShippingInfosResponse_CartInfo } from '@proto/store/api/store/cart';
import { Prices } from '@helper/prices';
import { Orders } from '@helper/orders';
import { PaymentShippingInfo } from '@proto/store/api/common/common';
import { RouteHelper } from '@helper/route-paths';

import { Decimal } from 'decimal.js-light';

export class CommonCartInfo {
  constructor(
    public info: string,
    public price: string,
    public originPrice: string,
    public quantity: number,
    public redInfo: boolean,
    public promotionId: string,
    public selected: boolean,
    public skuId: string,
    public boughtBefore: boolean,
    public prohibitOverseaSale: boolean,
    public link: string,
    public category: string
  ) {}
}

export class CommonFooterInfo {
  constructor(
    public total: number,
    public originalPrice: string,
    public reduction: string,
    public totalPrice: string,
    public shippingFee: string,
    public feeDiff: string,
    public balanceDeduct: string
  ) {}
}

export class CartResult {
  constructor(
    public result: Map<string, CommonCartInfo>,
    public footerInfo: CommonFooterInfo,
    public warnList: Set<string>
  ) {}
}

export class Carts {
  static getUsdTotal(total?: string, exchangeRateUsdNtd?: string): string {
    return !total || !exchangeRateUsdNtd || new Decimal(exchangeRateUsdNtd).eq(0)
      ? ''
      : new Decimal(total).div(exchangeRateUsdNtd).toFixed(2);
  }

  static getCartResult(
    cart: Cart,
    selectedInfo: PaymentShippingInfo | undefined,
    cartInfo: GetShippingInfosResponse_CartInfo | undefined
  ): CartResult {
    const items = cart.items;
    const promotions = cart.promotions;
    const ret = new Map<string, CommonCartInfo>();
    Array.from(
      items
        .reduce((groupedItems, item) => {
          const pId = item.published ? item.promotionId : '';
          if (!groupedItems.get(pId)) {
            groupedItems.set(pId, []);
          }
          groupedItems.get(pId)?.push(item);
          return groupedItems;
        }, new Map<string, Cart_Item[]>())
        .entries()
    ).forEach(item => {
      const promotionId = item[0];
      const group = item[1];
      const promotion = promotions.find(promo => promo.id === promotionId);
      if (!promotion || !promotion.discount?.itemNum) {
        group.forEach(item => {
          ret.set(
            item.skuId,
            Carts.mapToCommonCartInfo(
              item,
              Prices.price(item.meta?.originPrice),
              Prices.price(item.meta?.originPrice),
              item.quantity,
              '',
              false,
              ''
            )
          );
        });
      } else {
        const reached =
          promotion.discount.itemNum === 1 ||
          group.filter(item => item.selected).reduce((sum, item) => sum + item.quantity, 0) >=
            promotion.discount.itemNum;
        group.forEach(item => {
          ret.set(
            item.skuId,
            Carts.mapToCommonCartInfo(
              item,
              reached && item.meta?.promotionPrice
                ? Prices.price(item.meta?.promotionPrice)
                : Prices.price(item.meta?.originPrice),
              reached ? Prices.price(item.meta?.originPrice) : '',
              item.quantity,
              reached ? promotion.name : $localize`未達活動條件，點此加購`,
              !reached,
              promotion.enabled ? promotion.id : ''
            )
          );
        });
      }
    });
    const originalTotal = items
      .filter(item => item.selected)
      .reduce(
        (sum, item) => sum.add(new Decimal(item.quantity).mul(Prices.price(item.meta?.originPrice))),
        new Decimal(0)
      );
    const totalPrice = Array.from(ret.values())
      .filter(item => item.selected)
      .reduce((sum, item) => sum.add(new Decimal(item.quantity).mul(item.price)), new Decimal(0));
    let shippingFee = '0';
    let feeDiff = '0';
    if (selectedInfo) {
      // 無門檻時 免運門檻差額 為空字串 需顯示運費
      shippingFee =
        !!selectedInfo.thresholdDiff && new Decimal(selectedInfo.thresholdDiff || '0').lessThanOrEqualTo(0)
          ? selectedInfo.promotionFee
          : selectedInfo.fee;
      feeDiff = selectedInfo.thresholdDiff;
    }
    const finalPrice = cartInfo
      ? new Decimal(cartInfo.originPriceTotal).minus(cartInfo.promotionDeduction).plus(shippingFee).toFixed(2)
      : totalPrice.plus(shippingFee).toFixed(2);
    let balance = '0';
    if (!Orders.isOversea(selectedInfo?.type)) {
      if (cartInfo?.balance) {
        balance = new Decimal(cartInfo.balance).greaterThanOrEqualTo(finalPrice) ? finalPrice : cartInfo.balance;
      } else if (cart.balance) {
        balance = new Decimal(cart.balance).greaterThanOrEqualTo(finalPrice) ? finalPrice : cart.balance;
      }
    }
    return new CartResult(
      Array.from(ret.entries()).reduce((cartInfoMap, [key, value]) => {
        cartInfoMap.set(
          key,
          new CommonCartInfo(
            value.info,
            !value.price ? '' : new Decimal(value.price).mul(value.quantity).toFixed(2),
            !value.originPrice ? '' : new Decimal(value.originPrice).mul(value.quantity).toFixed(2),
            value.quantity,
            value.redInfo,
            value.promotionId,
            value.selected,
            value.skuId,
            value.boughtBefore,
            value.prohibitOverseaSale,
            value.link,
            value.category
          )
        );
        return cartInfoMap;
      }, new Map<string, CommonCartInfo>()),
      new CommonFooterInfo(
        items.filter(item => item.selected).reduce((sum, item) => sum + item.quantity, 0),
        cartInfo ? cartInfo.originPriceTotal : originalTotal.toFixed(2),
        cartInfo ? cartInfo.promotionDeduction : originalTotal.sub(totalPrice).toFixed(2),
        new Decimal(finalPrice).minus(balance).toFixed(2),
        shippingFee,
        feeDiff,
        balance
      ),
      new Set(
        items
          .filter(item => item.selected && (!item.published || item.quantity > item.inventory))
          .map(item => item.skuId)
      )
    );
  }

  private static mapToCommonCartInfo(
    item: Cart_Item,
    price: string,
    originPrice: string,
    quantity: number,
    promotionName: string,
    redInfo: boolean,
    promotionId: string
  ): CommonCartInfo {
    let info: CommonCartInfo;
    if (!item.published) {
      info = new CommonCartInfo(
        $localize`商品已下架`,
        price,
        originPrice,
        quantity,
        true,
        '',
        item.selected,
        item.skuId,
        item.boughtBefore,
        item.prohibitOverseaSale,
        item.published ? RouteHelper.product(item.skuId) : '',
        item.meta?.big?.nameEn ?? ''
      );
    } else if (item.inventory <= 0) {
      info = new CommonCartInfo(
        $localize`庫存不足`,
        price,
        originPrice,
        quantity,
        true,
        '',
        item.selected,
        item.skuId,
        item.boughtBefore,
        item.prohibitOverseaSale,
        item.published ? RouteHelper.product(item.skuId) : '',
        item.meta?.big?.nameEn ?? ''
      );
    } else if (item.inventory < item.quantity) {
      info = new CommonCartInfo(
        $localize`庫存剩 ${item.inventory} 件`,
        price,
        originPrice,
        quantity,
        true,
        '',
        item.selected,
        item.skuId,
        item.boughtBefore,
        item.prohibitOverseaSale,
        item.published ? RouteHelper.product(item.skuId) : '',
        item.meta?.big?.nameEn ?? ''
      );
    } else {
      info = new CommonCartInfo(
        promotionName,
        price,
        originPrice,
        quantity,
        redInfo,
        promotionId,
        item.selected,
        item.skuId,
        item.boughtBefore,
        item.prohibitOverseaSale,
        item.published ? RouteHelper.product(item.skuId) : '',
        item.meta?.big?.nameEn ?? ''
      );
    }
    if (!item.published) {
      const price = item.meta?.originPrice ?? '';
      info.promotionId = '';
      info.originPrice = price;
      info.price = price;
    }
    return info;
  }
}
